import type { NearbyEdge, NearbyVertex } from './nearby-types';

/** Fixed colour name each vertex-flag combination maps to on the inspector map. */
export interface VertexStyle {
  /** CSS colour name / hex used for the circle outline. */
  color: string;
  /** Human-facing label surfaced in the sidebar (`"entrance"`, `"in-station"`, `"ordinary"`). */
  label: string;
}

/** Fixed colour name each surface classification maps to on the inspector map. */
export interface EdgeStyle {
  /** CSS colour name / hex used for the line stroke. */
  color: string;
  /** Human-facing label matching the wire's surface classification. */
  label: string;
}

/** Radius slider bounds — the inspector clamps the user-typed value into this window. */
export const RADIUS_MIN_M = 100;
export const RADIUS_MAX_M = 1000;
export const RADIUS_DEFAULT_M = 300;
/** Cap on vertices returned per query (see `DEFAULT_MAX_VERTICES` in `debug_nearby.rs`). */
export const MAX_VERTICES = 200;

/** Colour used when a surface is not in the styling table below. */
const EDGE_FALLBACK: EdgeStyle = { color: '#888888', label: 'unknown' };

/** Colour used when a flag combination is not classified. */
const VERTEX_ORDINARY: VertexStyle = { color: '#888888', label: 'ordinary' };
const VERTEX_STATION: VertexStyle = { color: '#3355ff', label: 'in-station' };
const VERTEX_ENTRANCE: VertexStyle = { color: '#ff4433', label: 'entrance' };

/**
 * Style bucket for `vertex` based on its flag labels — entrance beats in-station
 * beats ordinary, matching the layered semantics the walking crate assigns.
 */
export function vertexStyle(vertex: Pick<NearbyVertex, 'flags'>): VertexStyle {
  if (vertex.flags.includes('is_transit_entrance')) return VERTEX_ENTRANCE;
  if (vertex.flags.includes('in_station')) return VERTEX_STATION;
  return VERTEX_ORDINARY;
}

/** Immutable surface → colour lookup for {@link edgeStyle}. Rendered as class-name-safe hex. */
const EDGE_TABLE: readonly (readonly [string, EdgeStyle])[] = [
  ['Paved', { color: '#111111', label: 'paved' }],
  ['Cobblestone', { color: '#7a4a1c', label: 'cobblestone' }],
  ['Gravel', { color: '#c19a5b', label: 'gravel' }],
  ['Steps', { color: '#e53935', label: 'steps' }],
  ['Synthetic', { color: '#8e24aa', label: 'synthetic' }],
];

/** Style bucket for `edge.surface`; falls back to grey when the wire surface is unknown. */
export function edgeStyle(edge: Pick<NearbyEdge, 'surface'>): EdgeStyle {
  const match = EDGE_TABLE.find(([surface]) => surface === edge.surface);
  return match ? match[1] : EDGE_FALLBACK;
}

/** Clamps `input` into `[RADIUS_MIN_M, RADIUS_MAX_M]`, defaulting non-finite values. */
export function clampRadius(input: number): number {
  if (!Number.isFinite(input)) return RADIUS_DEFAULT_M;
  return Math.max(RADIUS_MIN_M, Math.min(RADIUS_MAX_M, input));
}

/**
 * Encodes a nearby-query centre plus its radius into the string map `apiGet`
 * feeds to `URLSearchParams`. `max_vertices` is pinned to {@link MAX_VERTICES}.
 */
export function nearbyQuery(
  centre: readonly [number, number],
  radiusM: number,
): Record<string, number> {
  return {
    lat: centre[0],
    lon: centre[1],
    radius_m: clampRadius(radiusM),
    max_vertices: MAX_VERTICES,
  };
}
